import { useCallback, useEffect, useRef } from 'react';
import {
  AssetCountersQueryVariables,
  Counter,
  CounterFilterInputObject,
  useAssetCountersIdsLazyQuery,
  useAssetCountersQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { ApolloError } from '@apollo/client/errors';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import { ListQueryHookProps } from '../types';

export type UseAssetCountersQueryHookResult = {
  collection: Counter[];
  error: ApolloError | undefined;
  fetchIds: () => Promise<FetchIdsResponse<unknown>>;
  firstLoading: boolean;
  loading: boolean;
  refetch: () => Promise<unknown>;
  totalCount: number;
  totalPages: number;
};

type Args = ListQueryHookProps & { assetId: string };

export default function useAssetCountersQueryHook({
  assetId,
  fetchPolicy = 'cache-and-network',
  paginationParams,
  orderingParams,
  searchQuery,
  skip,
  usePrevious = true,
  onError,
}: Args): UseAssetCountersQueryHookResult {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(orderingParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingParams,
    GetUrlParams.OrderingDirection,
  );
  const queryVariables: AssetCountersQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    assetId,
  };
  const filters: CounterFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'componentAssetName' },
        { fieldName: 'componentAssetCode' },
      ]),
    );
  }
  if (filters.length) {
    queryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    queryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const query = useAssetCountersQuery({
    fetchPolicy,
    variables: queryVariables,
    skip,
  });
  const [fetchIdsExecute] = useAssetCountersIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIdsVariables = {
    onlyIds: true,
    filters: queryVariables.filters,
    assetId,
  };
  const fetchIdsVariablesRef = useRef(fetchIdsVariables);
  fetchIdsVariablesRef.current = fetchIdsVariables;
  // Stable ref so fetchIds is safe as a useEffect dependency (no render loop).
  const fetchIds = useCallback(
    () => fetchIdsExecute({ variables: fetchIdsVariablesRef.current }),
    [fetchIdsExecute],
  );
  const { loading, data, previousData, refetch, error } = query;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  useEffect(() => {
    if (error) {
      onErrorRef.current?.(error);
    }
  }, [error]);
  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection = _get(queryData, 'data.collection', []) as Counter[];
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);

  return {
    fetchIds,
    refetch,
    collection,
    error,
    firstLoading,
    loading,
    totalPages,
    totalCount,
  };
}
